/**
 * What Copy puts on the clipboard for an assistant reply: the answer as it
 * reads on screen, not the model's raw output. Raw output carries citation
 * markers (@cite:N:"…":"<id>":"file.pdf"), visual tags (<Table columns=…>)
 * and Markdown syntax, none of which survive a paste into an email or a doc.
 *
 * Same clone-and-clean idea as printElement: the rendered text parts are
 * cloned, citations become [n] with a filename-only Sources list, Table
 * visuals stay real tables, other visuals become a one-line placeholder, and
 * app classes are dropped so mail clients don't inherit them.
 */ import { Testids } from '@mono/ui-selectors';
const TEXT_PART = `[data-testid="${Testids.assistant.messageText}"]`;
const BLOCK = /^(P|DIV|H[1-6]|LI|UL|OL|TABLE|BLOCKQUOTE|PRE)$/;
const LAYOUT = /^(P|DIV|H[1-6]|LI|UL|OL|TABLE|THEAD|TBODY|TR|BLOCKQUOTE|PRE)$/;
/** Plain text with the line breaks a reader expects; innerText has none on a detached clone. */ function plainText(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        const value = node.textContent ?? '';
        // Whitespace between blocks is layout, not content.
        return /^\s*$/.test(value) && LAYOUT.test(node.parentElement?.tagName ?? '') ? '' : value;
    }
    if (!(node instanceof Element)) return '';
    if (node.tagName === 'BR') return '\n';
    const inner = [
        ...node.childNodes
    ].map(plainText).join('');
    if (node.tagName === 'TD' || node.tagName === 'TH') return `${inner.trim()}\t`;
    if (node.tagName === 'TR') return `${inner.replace(/\t$/, '')}\n`;
    if (node.tagName === 'LI') return `\n- ${inner.trim()}`;
    return BLOCK.test(node.tagName) ? `\n${inner}\n` : inner;
}
function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (c)=>`&#${c.charCodeAt(0)};`);
}
/** The reply's text parts as clean HTML and plain text, or null when none rendered. */ export function copyRenderedMessage(body) {
    // A text part is the answer's Markdown plus its on-screen sources list; only
    // the Markdown is copied, since the copy carries its own Sources list.
    const parts = [
        ...body.querySelectorAll(TEXT_PART)
    ].map((part)=>part.firstElementChild).filter((el)=>el instanceof HTMLElement);
    if (parts.length === 0) return null;
    const root = body.ownerDocument.createElement('div');
    for (const part of parts)root.appendChild(part.cloneNode(true));
    const doc = root.ownerDocument;
    // Citations → [n], numbered in reading order, one number per source file.
    const sources = [];
    for (const badge of root.querySelectorAll('[data-citation-id]')){
        const source = badge.dataset.citationSource || 'Source';
        let n = sources.indexOf(source) + 1;
        if (n === 0) n = sources.push(source);
        badge.replaceWith(doc.createTextNode(`[${n}]`));
    }
    for (const visual of root.querySelectorAll('[data-visual]')){
        if (visual.dataset.visual === 'Table') continue;
        const note = doc.createElement('p');
        note.textContent = `[${visual.dataset.visual} — open in Specra]`;
        visual.replaceWith(note);
    }
    // Sortable table headers are buttons; keep their text, drop the button.
    for (const button of root.querySelectorAll('th button'))button.replaceWith(...button.childNodes);
    for (const n of root.querySelectorAll('button, input, [data-print-hide], [aria-hidden="true"]'))n.remove();
    for (const el of [
        root,
        ...root.querySelectorAll('*')
    ]){
        el.removeAttribute('class');
        el.removeAttribute('style');
    }
    let html = root.innerHTML;
    let text = plainText(root).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    if (sources.length > 0) {
        html += `<p>Sources</p><ol>${sources.map((s)=>`<li>${escapeHtml(s)}</li>`).join('')}</ol>`;
        text += `\n\nSources\n${sources.map((s, i)=>`[${i + 1}] ${s}`).join('\n')}`;
    }
    return {
        html,
        text
    };
}
/** Writes both formats; falls back to plain text where rich clipboard writes are unavailable. */ export async function writeCopiedMessage(copied, writePlain) {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([
                        copied.html
                    ], {
                        type: 'text/html'
                    }),
                    'text/plain': new Blob([
                        copied.text
                    ], {
                        type: 'text/plain'
                    })
                })
            ]);
            return true;
        } catch  {
        // Permission denied or unsupported type: plain text still beats nothing.
        }
    }
    return writePlain(copied.text);
}
